/**
 * A run of coding steps, showing the latest and hiding the rest.
 *
 * The assistant surface collapses a run behind a single summary ("Tool activity
 * · 8 steps") because there the tool work is a detour on the way to an answer.
 * Coding is the opposite: the work IS the answer, and what the user wants while
 * a turn runs is to see the step happening now — not a count of steps they would
 * have to open a drawer to read.
 *
 * So the newest step stays visible and everything before it folds into one line.
 * Expanding lays the whole run out in order, newest last, which is where the eye
 * already is.
 *
 * @module components/chat/messages/coding-tool-run
 */

import { ChevronDownIcon } from "lucide-react";
import { useState, type ReactNode } from "react";

import { cn } from "@/lib/utils";

/** What a run shows in a given state. Pure, so it can be tested without a DOM. */
export interface CodingToolRunPlan {
  /** Index of the first step to draw; the rest of the list follows it. */
  firstVisible: number;
  /** Label for the fold toggle, or undefined when there is nothing to fold. */
  toggleLabel?: string;
}

/**
 * Decides which steps a run draws.
 *
 * Collapsed, only the last step is drawn — never the first, which is the state
 * a reader has already moved past. A run of one has nothing to fold and so
 * offers no toggle at all, rather than a control that does nothing.
 */
export function planToolRun(stepCount: number, expanded: boolean): CodingToolRunPlan {
  if (stepCount <= 1) return { firstVisible: 0 };
  if (expanded) return { firstVisible: 0, toggleLabel: "Show fewer tool calls" };

  const hidden = stepCount - 1;
  return {
    firstVisible: stepCount - 1,
    toggleLabel: `+${hidden} previous tool ${hidden === 1 ? "call" : "calls"}`,
  };
}

export interface CodingToolRunProps {
  /** The run's steps, in the order they happened. */
  items: ReactNode[];
}

export function CodingToolRun({ items }: CodingToolRunProps) {
  const [expanded, setExpanded] = useState(false);

  if (items.length === 0) return null;

  const { firstVisible, toggleLabel } = planToolRun(items.length, expanded);

  return (
    <div className="my-1 flex flex-col" data-testid="coding-tool-run">
      {items.slice(firstVisible)}

      {toggleLabel !== undefined && (
        <button
          type="button"
          data-testid="coding-tool-run-toggle"
          onClick={() => setExpanded(!expanded)}
          className={cn(
            "flex items-center gap-1.5 self-start py-1 text-sm text-muted-foreground",
            "transition-colors hover:text-foreground",
          )}
        >
          <ChevronDownIcon
            className={cn("size-3.5 shrink-0 transition-transform", expanded && "rotate-180")}
          />
          {toggleLabel}
        </button>
      )}
    </div>
  );
}
